"use strict";

import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import AppStore from '../../../stores/AppStore';
import AzureMediaPlayer from '../../../lib/AzureMediaPlayer';
import Spinner from '../../ui/Spinner.react';

// initialize Azure media player
AzureMediaPlayer.initialize();

let ieTechOrder = ["silverlightSS", "azureHtml5JS", "html5FairPlayHLS", "html5"];

/**
 * AzurePrototypePlayer component
 */
export default class AzurePrototypePlayer extends React.Component {
    /**
     * React: propTypes
     */
    static propTypes = {
        title: PropTypes.string,
        autoPlay: PropTypes.bool,
        controls: PropTypes.bool,
        poster: PropTypes.string.isRequired,
        video: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        playlist: PropTypes.object
    };

    /**
     * React: defaultProps
     */
    static defaultProps = {
        autoPlay: false,
        controls: true
    };

    /**
     * React: state
     */
    state = {
        videoTitle: '',
        play: false,
        videoDuration: 0,
        currentSeconds: 0,
        currentTime: "00:00:00",
        progressInterval: null,
        logWatchInterval: null,
        volume: 100,
        showPoster: true,
        showPlaylist: false,
        showSubtitlesDropdown: false,
        errorNotification: null,
        isFullScreen: false,
        mouseMoveTimeout: 0,
        areControlsHidden: false,
        isPlayerReady: false
    };

    /**
     * Player instance
     */
    _player = null;

    /**
     * React: UNSAFE_componentWillMount
     */
    UNSAFE_componentWillMount() {
        if(this.props.title) {
            this.setState({
                videoTitle: this.props.title
            });
        }
    }

    /**
     * React: componentDidMount
     */
    componentDidMount() {
        var self = this;
        var selfNode = ReactDOM.findDOMNode(this);

        var ampPlayer = window.amp('azure-media-player',
            {
                autoplay: this.props.autoPlay,
                width: '100%',
                height: '100%',
                nativeControlsForTouch: false,
                techOrder: ieTechOrder
            },
            function() {
                this.addEventListener('canplaythrough', self._onPlayerReady);
                this.addEventListener('play', self._onVideoPlay);
                this.addEventListener('pause', self._onVideoPause);
                this.addEventListener('ended', self._finishedPlayback);
                this.addEventListener('error', self._handlePlayerError);
                this.addEventListener('fullscreenchange', self._isFullScreen);
            }
        );

        ampPlayer.src([{
            src: this.props.video,
            type: this.props.type
        }]);

        this._player = ampPlayer;

        selfNode.addEventListener('mousemove', this._handleControls);

        document.addEventListener('click', this._closeDropDowns, false);
    }

    /**
     * React: componentWillUnmount
     */
    componentWillUnmount() {
        var selfNode = ReactDOM.findDOMNode(this);

        this._player = null;
        selfNode.removeEventListener('mousemove', this._handleControls);

        document.removeEventListener('click', this._closeDropDowns, false);
    }

    /**
     * React: render
     */
    render() {
        var mpaClassName = 'azure-player'
            + (this.state.isFullScreen ? ' full-screen' : '')
            + (this.state.areControlsHidden ? ' hidden-controls' : '');

        var mpaPosterClassName = 'mpa-poster'
            + (!this.state.isPlayerReady ? ' hidden' : '');

        var mpaControlsClassName = 'mpa-controls'
            + (!this.state.isPlayerReady ? ' hidden' : '');

        return (
            <div className={mpaClassName}>
                {!this.state.isPlayerReady ? (
                    <div className="mpa-loading">
                        <Spinner />
                    </div>
                ) : null}

                <video id="azure-media-player" className="azuremediaplayer amp-default-skin">
                    {AppStore.translate('view.media_player.message.not_supported')}
                </video>

                {this.state.showPoster ? (
                    <div className={mpaPosterClassName} style={{ backgroundImage: 'url(' + this.props.poster + ')' }}></div>
                ) : null}

                <div className={mpaControlsClassName}>
                    <div className="mpa-controls-bar mpa-controls-top">
                        <div className="controls-title">
                            {this.state.videoTitle}
                        </div>
                        <div className="controls-subtitles mpa-controls-dropdown">
                            <button className={'mpa-controls-button' + (this.state.showSubtitlesDropdown ? ' active' : '')} onClick={this._toggleSubtitlesDropdown} ref="subtitlesButton">
                                <i className="ui-icon icon-player-audio" />
                            </button>
                            {this.state.showSubtitlesDropdown ? (
                                <ul className="mpa-dropdown" ref="subtitles">
                                    <li><span>{AppStore.translate('view.media_player.controls.subtitles')}</span></li>
                                    <li>
                                        <button className="active">
                                            <i className="ui-icon icon-check-sign" />
                                            Off
                                        </button>
                                    </li>
                                    <li><button>English</button></li>
                                    <li><button>German</button></li>
                                </ul>
                            ) : null}
                        </div>
                        {typeof this.props.playlist === 'object' ? (
                            <div className="controls-playlist">
                                <button className={'mpa-controls-button' + (this.state.showPlaylist ? ' active' : '')} onClick={this._togglePlaylist} ref="playlistButton">
                                    <i className="ui-icon icon-forma" />
                                </button>
                            </div>
                        ) : null}
                        <div className="controls-watchlist">
                            <button className="mpa-controls-button">
                                <i className="ui-icon icon-player-watchlist" />
                            </button>
                        </div>
                        <div className="controls-volume mpa-controls-slider">
                            <input type="range" value={this.state.volume} min="0" max="100" step="1"
                                   onChange={this._volumeChange} />
                        </div>
                    </div>
                    <div className="mpa-controls-bar mpa-controls-bottom">
                        <div className="controls-play">
                            {this.state.play ? (
                                <button className="mpa-controls-button" onClick={this._pauseVideo}>
                                    <i className="ui-icon icon-pause-two-lines" />
                                </button>
                            ) : (
                                <button className="mpa-controls-button" onClick={this._playVideo}>
                                    <i className="ui-icon icon-play-simple" />
                                </button>
                            )}
                        </div>
                        <div className="controls-progress-bar mpa-controls-slider">
                            <input type="range" value={this.state.currentSeconds} min="0" max={this.state.videoDuration}
                                   step="1" onChange={this._progressChange} />
                        </div>
                        <div className="controls-current-time">
                            {this.state.currentTime}
                        </div>
                        <div className="controls-full-screen">
                            <button className="mpa-controls-button" onClick={this._toggleFullScreen}>
                                <i className="ui-icon icon-player-fullscreen" />
                            </button>
                        </div>
                    </div>
                </div>

                {this.state.errorNotification ? (
                    <div className="mpa-notification">
                        <div className="mpa-notification-message">
                            {this.state.errorNotification}
                        </div>
                    </div>
                ) : null}

                {this.state.showPlaylist ? (
                    <div className="mpa-playlist" ref="playlist">
                        <ul>
                            <li>
                                <div className="mpa-playlist-head">Available streams</div>
                            </li>
                            {Object.keys(this.props.playlist).map((key) => {
                                return (
                                    <li key={key}>
                                        <div className="mpa-playlist-title">
                                            {this.props.playlist[key].title}
                                        </div>
                                        <div className="mpa-playlist-duration">
                                            {this.props.playlist[key].duration}
                                        </div>
                                        <div className="mpa-playlist-progress">
                                            <div className="mpa-playlist-progress-bar">
                                                <span style={{ width: this.props.playlist[key].progress + '%' }} />
                                            </div>
                                        </div>
                                        <div className="mpa-playlist-action">
                                            <button className="active" onClick={this._changeVideo.bind(null, this.props.playlist[key], this.props.playlist[key])}>
                                                <i className="ui-icon icon-play" />
                                            </button>
                                        </div>
                                        <div className="mpa-playlist-action">
                                            <button>
                                                <i className="ui-icon icon-details" />
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ) : null}
            </div>
        );
    }

    /**
     * On player ready
     *
     * @private
     */
    _onPlayerReady = () => {
        this.setState({
            isPlayerReady: true
        });
    };

    /**
     * Play video
     *
     * @private
     */
    _playVideo = () => {
        this._player.play();
    };

    /**
     * On video play
     *
     * @private
     */
    _onVideoPlay = () => {
        var getVideoDuration = Math.floor(this._player.duration());
        var setProgressInterval = setInterval(this._setCurrentTime, 1000);
        var setLogWatchInterval = setInterval(this._updateLogWatch, 60000);

        this.setState({
            videoDuration: getVideoDuration,
            play: true,
            progressInterval: setProgressInterval,
            logWatchInterval: setLogWatchInterval,
            showPoster: false
        }, function() {
            this._updateLogWatch();
            this._handleControls();
        });
    };

    /**
     * Pause video
     *
     * @private
     */
    _pauseVideo = () => {
        this._player.pause();
    };

    /**
     * On video pause
     *
     * @private
     */
    _onVideoPause = () => {
        clearInterval(this.state.progressInterval);
        clearInterval(this.state.logWatchInterval);

        this.setState({
            play: false,
            progressInterval: null,
            logWatchInterval: null
        }, function() {
            this._updateLogWatch();
            this._handleControls();
        });
    };

    /**
     * Progress change
     *
     * @private
     * @param {Object} event
     */
    _progressChange = (event) => {
        var progressValue = event.target.value;
        this._player.currentTime(parseFloat(progressValue));

        this.setState({
            currentSeconds: progressValue
        });
    };

    /**
     * Volume change
     *
     * @private
     * @param {Object} event
     */
    _volumeChange = (event) => {
        var volumeValue = event.target.value;
        this._player.volume(volumeValue / 100);

        this.setState({
            volume: volumeValue
        });
    };

    /**
     * Toggle full screen
     *
     * @private
     */
    _toggleFullScreen = () => {
        var player = this._player;

        player.isFullscreen()
            ? player.exitFullscreen()
            : player.enterFullscreen();
    };

    /**
     * Is full screen
     *
     * @private
     */
    _isFullScreen = () => {
        this.setState({
            isFullScreen: this._player.isFullscreen()
        }, function() {
            this._handleControls();
        });
    };

    /**
     * Handle controls
     *
     * @private
     */
    _handleControls = () => {
        clearTimeout(this.state.mouseMoveTimeout);

        this.setState({
            areControlsHidden: false
        });

        if(this.state.play && this.state.isFullScreen) {
            var self = this;

            var mouseMoveTimeout = setTimeout(function () {
                self.setState({
                    areControlsHidden: true
                });
            }, 4000);

            this.setState({
                mouseMoveTimeout: mouseMoveTimeout
            });
        }
    };

    /**
     * Toggle subtitles dropdown
     *
     * @private
     */
    _toggleSubtitlesDropdown = () => {
        this.setState({
            showSubtitlesDropdown: !this.state.showSubtitlesDropdown
        });
    };

    /**
     * Toggle playlist
     *
     * @private
     */
    _togglePlaylist = () => {
        this.setState({
            showPlaylist: !this.state.showPlaylist
        });
    };

    /**
     * Set current time
     *
     * @private
     */
    _setCurrentTime = () => {
        var currentSeconds = Math.floor(this._player.currentTime());
        var currentTime = this._convertSeconds(currentSeconds);

        this.setState({
            currentSeconds: currentSeconds,
            currentTime: currentTime
        });
    };

    /**
     * Update LogWatch
     *
     * @private
     */
    _updateLogWatch = () => {
        Math.floor(this._player.currentTime());
    };

    /**
     * Finish playback
     *
     * @private
     */
    _finishedPlayback = () => {
        this.setState({
            showPoster: true
        });
    };

    /**
     * Handle player error
     *
     * @private
     */
    _handlePlayerError = () => {
        this._onVideoPause();

        var error = this._player.error();
        var errorMessage = '';

        if(error.code && window.amp.errorCode.encryptErrDecrypterNotFound) {
            errorMessage = 'Your current browser does not support this movies content protection (DRM).';
        } else {
            errorMessage = error.message;
        }

        this.setState({
            currentSeconds: 0,
            currentTime: "00:00:00",
            errorNotification: errorMessage
        });
    };

    /**
     * Close drop downs on outside click
     *
     * @private
     */
    _closeDropDowns = (event) => {
        var target = event.target;

        if(this.state.showSubtitlesDropdown && !this.refs.subtitlesButton.contains(target) && !this.refs.subtitles.contains(target)) {
            this.setState({
                showSubtitlesDropdown: !this.state.showSubtitlesDropdown
            });
        } else if(this.state.showPlaylist && !this.refs.playlistButton.contains(target) && !this.refs.playlist.contains(target)) {
            this.setState({
                showPlaylist: !this.state.showPlaylist
            });
        }
    };

    /**
     * Change video
     *
     * @private
     * @param {Object} videoObject
     */
    _changeVideo = (videoObject) => {
        this._player.src({
            src: videoObject.video,
            type: videoObject.type
        });

        this.setState({
            videoTitle: videoObject.title,
            showPlaylist: false,
            errorNotification: null
        });
    };

    /**
     * Convert seconds
     *
     * @private
     * @param {number} totalSeconds
     */
    _convertSeconds = (totalSeconds) => {
        var hours = Math.floor(totalSeconds / 3600);
        var minutes = Math.floor((totalSeconds - (hours * 3600)) / 60);
        var seconds = totalSeconds - (hours * 3600) - (minutes * 60);

        seconds = Math.round(seconds * 100) / 100;

        var result = (hours < 10 ? '0' + hours : hours);
        result += ':' + (minutes < 10 ? '0' + minutes : minutes);
        result += ':' + (seconds < 10 ? '0' + seconds : seconds);

        return result;
    };
}
